import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Stack,
    Slider,
    InputAdornment,
    Radio,
    RadioGroup,
    FormControlLabel,
} from '@mui/material';
import { Line, GlobalContext } from '../../../context/context';
import { handleLineStyleEdit } from '../handlers/lineStyleHandlers';

interface LineStyleModalProps {
    open: boolean;
    onClose: () => void;
    line: Line | null;
    global: GlobalContext;
    editLayer: (updatedLayer: any, layerId: number) => void;
    setSelectedLine: (line: Line | null) => void;
    setIsLineModalOpen: (open: boolean) => void;
}

const colorPalette = [
    '#FF0000', // Red
    '#FF4500', // Orange Red
    '#FFA500', // Orange
    '#FFD700', // Gold
    '#FFFF00', // Yellow
    '#32CD32', // Lime Green
    '#008000', // Green
    '#00CED1', // Dark Turquoise
    '#0000FF', // Blue
    '#4B0082', // Indigo
    '#800080', // Purple
    '#FF1493', // Deep Pink
    '#A52A2A', // Brown
    '#000000', // Black
    '#808080', // Gray
    '#FFFFFF', // White
];

const arrowOptions = [
    { value: 'none', labelKey: 'none' },
    { value: 'start', labelKey: 'start' },
    { value: 'end', labelKey: 'end' },
    { value: 'both', labelKey: 'both' },
    { value: 'middle', labelKey: 'middle' },
    { value: 'all', labelKey: 'all' }
];

// Arrow style preview component
function ArrowPreview({ color, arrowStyle }: { color: string; arrowStyle: string }) {
    // Arrow marker definition
    const markerId = `arrowhead-${color.replace('#', '')}`;
    // Arrow positions
    const lineStart = { x: 10, y: 10 };
    const lineEnd = { x: 70, y: 10 };
    const mid = { x: 40, y: 10 };
    // For 'all', use 4 evenly spaced arrows
    const allPoints = [16, 32, 48, 64];
    // Which markers to use
    let markerStart = false, markerEnd = false, markerMid = false, markerAll = false;
    if (arrowStyle === 'start') markerStart = true;
    if (arrowStyle === 'end') markerEnd = true;
    if (arrowStyle === 'both') { markerStart = true; markerEnd = true; }
    if (arrowStyle === 'middle') markerMid = true;
    if (arrowStyle === 'all') markerAll = true;

    return (
        <svg width="80" height="20" style={{ display: 'block' }}>
            <defs>
                <marker
                    id={markerId}
                    viewBox="0 0 10 10"
                    refX="5"
                    refY="5"
                    markerWidth="4"
                    markerHeight="4"
                    orient="auto-start-reverse"
                >
                    <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
                </marker>
            </defs>
            <line
                x1={lineStart.x}
                y1={lineStart.y}
                x2={lineEnd.x}
                y2={lineEnd.y}
                stroke={color}
                strokeWidth="2"
                markerStart={markerStart ? `url(#${markerId})` : undefined}
                markerEnd={markerEnd ? `url(#${markerId})` : undefined}
                markerMid={markerMid ? `url(#${markerId})` : undefined}
            />
            {markerAll && allPoints.map((x, i) => (
                <line
                    key={i}
                    x1={x - 2}
                    y1={lineStart.y}
                    x2={x + 2}
                    y2={lineStart.y}
                    stroke={color}
                    strokeWidth="2"
                    markerEnd={`url(#${markerId})`}
                />
            ))}
        </svg>
    );
}

export default function LineStyleModal({ 
    open, 
    onClose, 
    line,
    global,
    editLayer,
    setSelectedLine,
    setIsLineModalOpen
}: LineStyleModalProps) {
    const { t } = useTranslation(['mapDialogs', 'common']);
    const [editedLine, setEditedLine] = React.useState<Line | null>(null);

    React.useEffect(() => {
        if (line) {
            setEditedLine({
                ...line,
            });
        }
    }, [line]);

    if (!editedLine) return null;

    const handleSave = () => {
        if (editedLine) {
            handleLineStyleEdit({
                line: editedLine,
                global,
                editLayer,
                setSelectedLine,
                setIsLineModalOpen
            });
        }
    };

    const handleBorderWidthChange = (newWidth: number) => {
        if (newWidth >= 0 && newWidth <= 10) {
            setEditedLine({ ...editedLine, borderWidth: newWidth });
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t('mapDialogs:lineStyle.title')}</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    <TextField
                        label={t('mapDialogs:lineStyle.lineName')}
                        value={editedLine.name}
                        onChange={(e) => setEditedLine({ ...editedLine, name: e.target.value })}
                        fullWidth
                    />

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:lineStyle.lineColor')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {colorPalette.map((color) => (
                                <Box
                                    key={color}
                                    onClick={() => setEditedLine({ ...editedLine, colour: color })}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        backgroundColor: color,
                                        border: editedLine.colour === color ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            opacity: 0.8,
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" gutterBottom>
                                {t('mapDialogs:lineStyle.customLineColor')}
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor: editedLine.colour,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                    }}
                                />
                                <input
                                    type="color"
                                    value={editedLine.colour}
                                    onChange={(e) => setEditedLine({ ...editedLine, colour: e.target.value })}
                                    style={{ width: '100%' }}
                                />
                            </Stack>
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:lineStyle.borderColor')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {colorPalette.map((color) => (
                                <Box
                                    key={color}
                                    onClick={() => setEditedLine({ ...editedLine, borderColour: color })}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        backgroundColor: color,
                                        border: editedLine.borderColour === color ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            opacity: 0.8,
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" gutterBottom>
                                {t('mapDialogs:lineStyle.customBorderColor')}
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor: editedLine.borderColour,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                    }}
                                />
                                <input
                                    type="color"
                                    value={editedLine.borderColour}
                                    onChange={(e) => setEditedLine({ ...editedLine, borderColour: e.target.value })}
                                    style={{ width: '100%' }}
                                />
                            </Stack>
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:lineStyle.borderWidth')}
                        </Typography>
                        <Stack spacing={2} direction="row" alignItems="center">
                            <Slider
                                value={editedLine.borderWidth}
                                onChange={(_, value) => handleBorderWidthChange(value as number)}
                                min={0}
                                max={10}
                                step={1}
                                sx={{ flexGrow: 1 }}
                            />
                            <TextField
                                value={editedLine.borderWidth}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value);
                                    if (!isNaN(value)) {
                                        handleBorderWidthChange(value);
                                    }
                                }}
                                type="number"
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">px</InputAdornment>,
                                }}
                                sx={{ width: 100 }}
                                size="small"
                            />
                        </Stack>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:lineStyle.arrowStyle')}
                        </Typography>
                        <RadioGroup
                            value={editedLine.arrowStyle || 'none'}
                            onChange={(e) => setEditedLine({ ...editedLine, arrowStyle: e.target.value })}
                            row
                            sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}
                        >
                            {arrowOptions.map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={<Radio />}
                                    label={
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                            <ArrowPreview color={editedLine.borderColour} arrowStyle={option.value} />
                                            <Typography variant="caption">{t(`mapDialogs:lineStyle.arrow.${option.labelKey}`)}</Typography>
                                        </Box>
                                    }
                                    sx={{ margin: 0 }}
                                />
                            ))}
                        </RadioGroup>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common:cancel')}</Button>
                <Button onClick={handleSave} variant="contained">
                    {t('mapDialogs:common.saveChanges')}
                </Button>
            </DialogActions>
        </Dialog>
    );
} 